'use client'

import * as React from 'react';
import { NovaAIChatInner } from './index';
import { NovaAIChatProps, SchemaResponse } from './types';
import { getSchema } from './utils';
import { Sparkles } from 'lucide-react';
import { supernovaColor } from './types';
import { cn } from '@/components/ui';

/**
 * NovaAIChat Component
 * 
 * A chat interface for interacting with Nova AI for data analysis and query building.
 * This component fetches the schema before rendering the inner chat component.
 */
export function NovaAIChat(props: NovaAIChatProps) {
  const { engine } = props;
  
  // State for schema initialization
  const [isLoading, setIsLoading] = React.useState<boolean>(true);
  const [error, setError] = React.useState<string | null>(null);
  const [schema, setSchema] = React.useState<SchemaResponse | null>(null);
  
  // Fetch schema on mount
  React.useEffect(() => {
    let isMounted = true;
    
    const fetchSchema = async () => {
      try {
        const schemaData = await getSchema(engine);
        
        if (isMounted) {
          setSchema(schemaData);
          setIsLoading(false);
        }
      } catch (err) {
        console.error('Error fetching schema:', err);
        
        if (isMounted) {
          setError('Failed to fetch schema. Please try again.');
          setIsLoading(false);
        }
      }
    };
    
    fetchSchema();
    
    return () => {
      isMounted = false;
    };
  }, [engine]);
  
  // Show loading state
  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center h-full p-4 text-center bg-card">
        <div className="animate-pulse mb-4">
          <Sparkles className="h-8 w-8 text-supernova-purple" />
        </div>
        <p className="text-sm font-medium text-supernova-purple">
          Loading schema data...
        </p>
        <p className="text-xs mt-2 text-muted-foreground">
          Nova AI is preparing to analyze your data
        </p>
      </div>
    );
  }
  
  // Show error state
  if (error) {
    return (
      <div className="p-4 text-center bg-card">
        <p className="text-destructive mb-2">{error}</p>
        <button 
          className={cn("px-3 py-1.5 rounded bg-supernova-purple text-white font-medium text-sm")}
          onClick={() => window.location.reload()}
        >
          Retry
        </button>
      </div>
    );
  }
  
  // Render NovaAIChatInner with schema
  return <NovaAIChatInner {...props} initialSchema={schema} />;
} 